
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";

import enUK from "@/locales/invoicing/en-UK.json";

import MutateEntity from "@/components/CRUXEntity/MutateEntity.vue";

import { UPDATE_INVOICE } from "@/graphql/invoice/mutations";

import {
  Invoice,
  InvoiceCollectionStatus,
  InvoicingRole,
} from "@/helpers/invoice/typesDefinition";
import { GET_USERS_BY_ROLES, GET_USER } from "@/graphql/user/queries";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";
import { fullName } from "@/helpers/user/utils";
import { User } from "@/helpers/user/typesDefinition";
import BasicButton from "@/components/BasicButton.vue";
import AddOffered from "@/components/offered/AddOffered.vue";
import { formatPrice } from "@/helpers/utils";
import { isAdmin } from "@/helpers/router/queries";

type MessageSchema = typeof enUK;

export default defineComponent({
  setup(props) {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();

    const { result: userResult } = useQuery(GET_USER);
    const user = computed(() => userResult.value?.user);
    const isUserAdmin = computed(() => isAdmin(user.value?.accessRights));

    const { result: getUsersResult, onError: onGetUsersError } = useQuery(
      GET_USERS_BY_ROLES,
      { roles: [InvoicingRole.ADMIN, InvoicingRole.FINANCIAL] },
    );

    const usersList = computed(() => getUsersResult.value?.users ?? []);

    const formOptions = computed(() => {
      return {
        collectionStatus: {
          [InvoiceCollectionStatus.EXPECTED_PAYMENT]: t(
            "collectionStatus." + InvoiceCollectionStatus.EXPECTED_PAYMENT,
          ),
          [InvoiceCollectionStatus.ABANDONED]: t(
            "collectionStatus." + InvoiceCollectionStatus.ABANDONED,
          ),
          [InvoiceCollectionStatus.DOUBTFUL]: t(
            "collectionStatus." + InvoiceCollectionStatus.DOUBTFUL,
          ),
          [InvoiceCollectionStatus.CREDIT_NOTE_TO_ISSUE]: t(
            "collectionStatus." + InvoiceCollectionStatus.CREDIT_NOTE_TO_ISSUE,
          ),
          [InvoiceCollectionStatus.FAR_FUTURE]: t(
            "collectionStatus." + InvoiceCollectionStatus.FAR_FUTURE,
          ),
        },
        assignedTo: usersList.value
          .filter((user: User) => !!user.firstName)
          .reduce((acc: { [key: string]: string }, user: User) => {
            acc[user.id] = fullName(user);
            return acc;
          }, {}),
      };
    });

    onGetUsersError((error) => {
      logErrorMessages(error);
      toast.error(t("errors.getUsersByRole"));
    });

    const isAddOfferedModalVisible = ref(false);

    const showAddOfferedModal = () => {
      if (!props.invoice.projectId || !isUserAdmin.value) {
        return;
      }

      isAddOfferedModalVisible.value = true;
    };

    const closeAddOfferedModal = () => {
      isAddOfferedModalVisible.value = false;
    };

    const {
      mutate: updateInvoiceAfterOffer,
      onError: onErrorUpdateInvoiceAfterOffer,
    } = useMutation(UPDATE_INVOICE);

    onErrorUpdateInvoiceAfterOffer((error) => {
      logErrorMessages(error);
      toast.error(t("errors.edit"));
    });

    const handleOfferedCreated = async (offered: { amountNet: number }) => {
      const currentComment = props.invoice.comment || "";
      const offerComment = `Todo: credit note (${formatPrice(
        offered.amountNet,
      )})`;

      await updateInvoiceAfterOffer({
        id: props.invoice.id,
        collectionStatus: InvoiceCollectionStatus.CREDIT_NOTE_TO_ISSUE,
        comment: currentComment
          ? `${currentComment}\n${offerComment}`
          : offerComment,
      });

      closeAddOfferedModal();
    };

    return {
      t,
      formOptions,
      isUserAdmin,
      isAddOfferedModalVisible,
      showAddOfferedModal,
      closeAddOfferedModal,
      handleOfferedCreated,
    };
  },
  props: {
    invoice: {
      type: Object as PropType<Invoice>,
      required: true,
    },
  },
  emits: ["close"],
  methods: {
    closeModal(): void {
      this.$emit("close");
    },
  },
  data() {
    return {
      enUK,
      validationRules: computed(() => ({
        id: {},
        assignedTo: {},
        collectionStatus: {},
        clientName: {},
        comment: {},
        promisedDate: {},
        communication: {},
      })),
      UPDATE_INVOICE,
      messages: {
        formButtonLabel: this.t("buttons.edit"),
        error: this.t("errors.edit"),
        success: this.t("success.edit"),
      },
      refetchQueries: "all",
      // FIXME: The cache for GET_OPENED_INVOICES query is not updating as it should when filter param is involved..
      // FIXME: (also we should be more selective than refetch "all").
    };
  },
  components: {
    BasicButton,
    MutateEntity,
    AddOffered,
  },
});
